import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { PageHeader, Section, StatCard, Spinner, RiskBadge } from "@/components/ui-parts";
import { Download } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function EnterpriseReport() {
  const { withScope, activeTenantId, currentTenant } = useTenant();
  const [stats, setStats] = useState(null);
  const [coverage, setCoverage] = useState([]);
  const [org, setOrg] = useState({});
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats", { params: withScope() }),
      api.get("/dashboard/framework-coverage", { params: withScope() }),
      api.get("/settings/organization", { params: withScope() }),
    ]).then(([a, b, c]) => { setStats(a.data); setCoverage(b.data); setOrg(c.data || {}); });
    // eslint-disable-next-line
  }, [activeTenantId]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      const scope = withScope();
      if (scope.tenant_id) params.set("tenant_id", scope.tenant_id);
      const url = `${BASE}/api/dashboard/enterprise-report/pdf${params.toString() ? "?" + params : ""}`;
      const res = await fetch(url, {
        credentials: "include",
        headers: localStorage.getItem("gacrf_token") ? { Authorization: `Bearer ${localStorage.getItem("gacrf_token")}` } : {},
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `GACRF_enterprise_report_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (e) { alert(e.message); }
    finally { setDownloading(false); }
  };

  if (!stats) return <div className="p-8"><Spinner /></div>;

  return (
    <div className="p-8 max-w-[1600px]" data-testid="report-page">
      <PageHeader
        eyebrow="Reports"
        title="GACRF® Enterprise Assessment Report"
        description="Formal enterprise-level assessment output including executive summary, domain scores, findings and framework coverage."
        actions={
          <button className="btn btn-primary btn-sm" onClick={downloadPdf} disabled={downloading} data-testid="export-enterprise-pdf-btn">
            <Download size={12} /> {downloading ? "Exporting..." : "Export PDF"}
          </button>
        }
      />

      {/* Header block */}
      <Section title="Report Metadata">
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 mb-1">Organization</div><div className="text-slate-100">{org.orgName || currentTenant?.name}</div></div>
          <div><div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 mb-1">Sector</div><div className="text-slate-100">{org.orgSector || currentTenant?.sector}</div></div>
          <div><div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 mb-1">Assessor</div><div className="text-slate-100">{org.orgAssessor || "GlobeSec"}</div></div>
          <div><div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 mb-1">Date</div><div className="text-slate-100 font-mono">{org.orgAssessmentDate || "—"}</div></div>
        </div>
      </Section>

      {/* Executive Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard tone="brand" label="ECRI" value={stats.ecri} subValue="/ 100" />
        <StatCard label="Certification" value={stats.certification} />
        <StatCard label="Overall Risk" value={stats.risk_posture} />
        <StatCard label="Maturity" value={stats.avg_score.toFixed(2)} subValue={stats.maturity} />
      </div>

      <Section title="1. Executive Summary">
        <div className="p-5 text-sm text-slate-300 leading-relaxed">
          {org.orgName || currentTenant?.name} için yapılan GACRF değerlendirmesinde {stats.assessed}/{stats.total} requirement skorlanmıştır.
          Mevcut Executive Cyber Resilience Index {stats.ecri}/100 olarak hesaplanmıştır.
          Genel olgunluk seviyesi <strong>{stats.maturity}</strong>, sertifikasyon hazırlık seviyesi <strong>{stats.certification}</strong>,
          genel risk görünümü ise <strong>{stats.risk_posture}</strong> olarak değerlendirilmiştir.
          Öncelikli odak alanı, yüksek riskli ve düşük skorlu requirement kayıtlarının kapatılmasıdır.
        </div>
      </Section>

      <Section title="2. Domain Scores">
        <table className="data-table">
          <thead><tr><th>Domain</th><th>Score / 5</th><th>ECRI Contribution</th><th>Interpretation</th></tr></thead>
          <tbody>
            {stats.domains.map((d) => (
              <tr key={d.domain}>
                <td className="text-slate-200">{d.domain}</td>
                <td className="mono">{d.score.toFixed(2)}</td>
                <td className="mono">{d.ecri} / 100</td>
                <td>{d.maturity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title="3. Top 10 High Risk Findings">
        <table className="data-table">
          <thead><tr><th>Requirement</th><th>Domain</th><th>Capability</th><th>Risk</th><th>Score</th><th>Gap</th></tr></thead>
          <tbody>
            {stats.top_gaps.map((r) => (
              <tr key={r.requirementId}>
                <td className="mono">{r.requirementId}</td>
                <td>{r.domain}</td>
                <td>{r.capability}</td>
                <td><RiskBadge level={r.riskLevel} /></td>
                <td className="mono">{r.score.toFixed(1)}</td>
                <td>{r.gap}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title="4. Framework Coverage">
        <table className="data-table">
          <thead><tr><th>Framework</th><th>Mapped Requirements</th><th>Average Score</th><th>Coverage Status</th></tr></thead>
          <tbody>
            {coverage.map((r) => (
              <tr key={r.framework}>
                <td className="text-slate-200">{r.framework}</td>
                <td className="mono">{r.mapped}</td>
                <td className="mono">{r.avg_score.toFixed(2)}</td>
                <td><span className={`badge ${r.status === "Strong" ? "badge-success" : r.status === "Moderate" ? "badge-medium" : "badge-critical"}`}>{r.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title="5. Executive Recommendations">
        <ul className="p-5 text-sm text-slate-300 space-y-2 list-disc pl-8">
          <li>Prioritize improvement in weakest domains: {stats.domains.slice().sort((a,b)=>a.score-b.score).slice(0,3).map(d=>d.domain).join(", ")}.</li>
          <li>Assign accountable owners for all High and Critical risk requirements.</li>
          <li>Complete missing evidence links and validate evidence quality before certification review.</li>
          <li>Elevate requirements with score below 3 to at least Managed/Integrated level.</li>
          <li>Track ECRI trend as a quarterly board-level cyber resilience KPI.</li>
          <li>Strengthen Continuous Assurance for controls with implementation evidence but low effectiveness scores.</li>
          <li>Align improvement actions to a 30/60/90-day execution plan with clear owners and KPIs.</li>
        </ul>
      </Section>
    </div>
  );
}
